import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Check, Trophy, Trash2, Wand2, ChevronRight, StickyNote, Copy } from "lucide-react";
import { toast } from "sonner";
import PaywallModal from "@/components/PaywallModal";
import { formatMoney } from "@/lib/money";
import { LeadScoreBadge, BAND_STYLES } from "./LeadScoreBadge";

const copyText = (t) => {
  navigator.clipboard?.writeText(typeof t === "string" ? t : JSON.stringify(t, null, 2));
  toast.success("Copied to clipboard");
};

const OUTREACH_TABS = [
  { key: "cold_email", label: "Email" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "phone_script", label: "Call Script" },
  { key: "followups", label: "Follow-ups" },
  { key: "proposal", label: "Proposal" },
  { key: "testimonial_request", label: "Testimonial" },
  { key: "referral_request", label: "Referral" },
];

function OutreachContent({ outreach, tab }) {
  const v = outreach?.[tab];
  if (!v) return <p className="text-sm text-muted-foreground">Not generated yet.</p>;
  if (tab === "cold_email") {
    return (
      <div className="space-y-2 text-sm">
        <p><span className="font-medium">Subject:</span> {v.subject}</p>
        <p className="whitespace-pre-wrap text-muted-foreground">{v.body}</p>
      </div>
    );
  }
  if (tab === "followups") {
    return (
      <div className="space-y-3 text-sm">
        {(v || []).map((f, i) => (
          <div key={i} className="rounded-xl border border-border bg-background p-3">
            <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">Day {[2, 5, 10][i] || "+"}</p>
            <p className="whitespace-pre-wrap text-muted-foreground">{f}</p>
          </div>
        ))}
      </div>
    );
  }
  return <p className="whitespace-pre-wrap text-sm text-muted-foreground">{v}</p>;
}

export const PipelineTab = ({ leads, stages, onRefresh }) => {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [winOpen, setWinOpen] = useState(false);
  const [winAmount, setWinAmount] = useState("");
  const [winNote, setWinNote] = useState("");
  const [noteInput, setNoteInput] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [outreachTab, setOutreachTab] = useState("cold_email");
  const [paywall, setPaywall] = useState(null);

  const shown = (leads || []).filter((l) => stageFilter === "all" || l.stage === stageFilter);
  const lead = (leads || []).find((l) => l.lead_id === selectedId) || shown[0] || null;
  const stageLabel = (key) => (stages || []).find((s) => s.key === key)?.label || key;

  const completeStep = async (stepId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/truffl/leads/${lead.lead_id}/steps/${stepId}/complete`);
      toast.success(`+${data.xp_awarded} XP — pipeline: ${data.stage_label}`);
      (data.new_achievements || []).forEach((a) => toast.success(`🏆 ${a.label}`, { description: a.desc }));
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not complete step");
    } finally {
      setBusy(false);
    }
  };

  const generateFor = async () => {
    setBusy(true);
    toast.info("Generating personalised outreach…");
    try {
      await api.post("/truffl/generate", { lead_ids: [lead.lead_id] });
      toast.success("Mission & outreach ready 🎯");
      onRefresh?.();
    } catch (err) {
      if (err?.response?.status === 402) {
        setPaywall({ kind: "truffl_generate_limit", title: "Unlimited AI outreach",
          body: err.response.data?.detail || "Upgrade to Pro to generate unlimited outreach missions." });
      } else {
        toast.error(err?.response?.data?.detail || "Generation failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const markWon = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/truffl/leads/${lead.lead_id}/win`, {
        amount: Number(winAmount || 0), note: winNote || undefined,
      });
      toast.success(`Client won! +${data.xp_awarded} XP — all your stats just updated 🎉`);
      (data.new_achievements || []).forEach((a) => toast.success(`🏆 ${a.label}`, { description: a.desc }));
      setWinOpen(false); setWinAmount(""); setWinNote("");
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not mark as won");
    } finally {
      setBusy(false);
    }
  };

  const setStage = async (stage) => {
    try {
      await api.patch(`/truffl/leads/${lead.lead_id}/stage`, { stage });
      toast.success(`Stage: ${stageLabel(stage)}`);
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update stage");
    }
  };

  const addNote = async () => {
    if (!noteInput.trim()) return;
    try {
      await api.post(`/truffl/leads/${lead.lead_id}/notes`, { note: noteInput.trim() });
      setNoteInput("");
      onRefresh?.();
    } catch { toast.error("Could not save note"); }
  };

  const removeLead = async (id) => {
    try {
      await api.delete(`/truffl/leads/${id}`);
      toast.success("Lead removed");
      if (selectedId === id) setSelectedId(null);
      onRefresh?.();
    } catch { toast.error("Could not remove lead"); }
  };

  if (!leads?.length) {
    return (
      <div className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="pipeline-empty-state">
        <p className="font-display text-lg">{t("truffl.empty_title")}</p>
        <p className="mt-1 text-sm text-muted-foreground">{t("truffl.empty_sub")}</p>
      </div>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
      <PaywallModal open={!!paywall} onOpenChange={(v) => !v && setPaywall(null)}
        kind={paywall?.kind} title={paywall?.title} body={paywall?.body} context="truffl" />
      <div className="space-y-3">
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="h-9" data-testid="pipeline-stage-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            {(stages || []).map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="space-y-2" data-testid="pipeline-lead-list">
          {shown.map((l) => {
            const bs = BAND_STYLES[l.band] || BAND_STYLES.low;
            const active = lead?.lead_id === l.lead_id;
            return (
              <button key={l.lead_id} onClick={() => setSelectedId(l.lead_id)}
                data-testid="pipeline-lead-item"
                className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${active ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"}`}>
                <LeadScoreBadge score={l.score} band={l.band} size={40} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{l.name}</p>
                  <p className="text-xs text-muted-foreground">{stageLabel(l.stage)}{l.won_amount ? ` · ${formatMoney(l.won_amount)}` : ""}</p>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            );
          })}
        </div>
      </div>

      {lead && (
        <div className="space-y-4" data-testid="pipeline-lead-detail">
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-center gap-4">
                <LeadScoreBadge score={lead.score} band={lead.band} size={56} />
                <div>
                  <h3 className="font-display text-xl">{lead.name}</h3>
                  <p className="text-xs text-muted-foreground">{lead.category || "Local business"} · {lead.city}</p>
                  <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs ${(BAND_STYLES[lead.band] || BAND_STYLES.low).chip}`}>
                    {stageLabel(lead.stage)}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                {!lead.won_at && (
                  <Button size="sm" onClick={() => setWinOpen(true)}
                    className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="mark-won-btn">
                    <Trophy className="mr-1 h-3.5 w-3.5" /> {t("truffl.mark_won")}
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => removeLead(lead.lead_id)} data-testid="delete-lead-btn">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            {!lead.won_at && (
              <div className="mt-4 flex items-center gap-2">
                <Label className="text-xs text-muted-foreground">Move stage:</Label>
                <Select value={lead.stage} onValueChange={setStage}>
                  <SelectTrigger className="h-8 w-52 text-xs" data-testid="lead-stage-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(stages || []).filter((s) => !["won", "revenue_logged"].includes(s.key)).map((s) => (
                      <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {!lead.steps?.length ? (
            <div className="rounded-2xl border border-dashed border-border p-8 text-center">
              <p className="text-sm text-muted-foreground">No mission yet for this lead.</p>
              <Button className="mt-3" disabled={busy} onClick={generateFor} data-testid="generate-for-lead-btn">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />}
                Generate Outreach Mission
              </Button>
            </div>
          ) : (
            <>
              <div className="rounded-2xl border border-border bg-card p-6">
                <h4 className="font-display text-lg">Mission steps</h4>
                <div className="mt-4 space-y-2" data-testid="mission-steps">
                  {lead.steps.map((s) => (
                    <div key={s.step_id} className={`flex items-center gap-3 rounded-xl border border-border bg-background px-3 py-2.5 ${s.done ? "opacity-60" : ""}`} data-testid="mission-step">
                      <button
                        disabled={busy || s.done}
                        onClick={() => completeStep(s.step_id)}
                        data-testid="step-complete-btn"
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors ${s.done ? "border-emerald-500 bg-emerald-500 text-white" : "border-border hover:border-primary"}`}>
                        {s.done && <Check className="h-3.5 w-3.5" />}
                      </button>
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm ${s.done ? "line-through" : ""}`}>{s.title}</p>
                        <p className="text-xs text-muted-foreground">Day {s.day} · +{s.xp} XP{s.skipped ? " · skipped" : ""}</p>
                      </div>
                      {s.content_key && (
                        <button onClick={() => setOutreachTab(s.content_key)} className="text-xs text-primary hover:underline" data-testid="step-view-content-btn">
                          View content
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="flex items-center justify-between">
                  <h4 className="font-display text-lg">Outreach content</h4>
                  <Button size="sm" variant="ghost" onClick={() => copyText(lead.outreach?.[outreachTab])} data-testid="copy-outreach-btn">
                    <Copy className="mr-1 h-3.5 w-3.5" /> Copy
                  </Button>
                </div>
                {lead.outreach?.business_summary && (
                  <p className="mt-2 rounded-xl bg-secondary/60 p-3 text-xs text-muted-foreground" data-testid="business-summary">
                    {lead.outreach.business_summary}
                  </p>
                )}
                <Tabs value={outreachTab} onValueChange={setOutreachTab} className="mt-4">
                  <TabsList className="flex h-auto flex-wrap justify-start">
                    {OUTREACH_TABS.map((t) => (
                      <TabsTrigger key={t.key} value={t.key} className="text-xs" data-testid={`outreach-tab-${t.key}`}>{t.label}</TabsTrigger>
                    ))}
                  </TabsList>
                  {OUTREACH_TABS.map((t) => (
                    <TabsContent key={t.key} value={t.key} className="mt-4">
                      <OutreachContent outreach={lead.outreach} tab={t.key} />
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            </>
          )}

          <div className="rounded-2xl border border-border bg-card p-6">
            <h4 className="flex items-center gap-2 font-display text-lg"><StickyNote className="h-4 w-4" /> Notes</h4>
            <div className="mt-3 flex gap-2">
              <Input value={noteInput} onChange={(e) => setNoteInput(e.target.value)} placeholder="Add a note…" data-testid="lead-note-input" />
              <Button size="sm" onClick={addNote} data-testid="lead-note-save-btn">Save</Button>
            </div>
            {(lead.notes || []).slice().reverse().map((n, i) => (
              <p key={i} className="mt-2 rounded-xl bg-secondary/50 px-3 py-2 text-sm text-muted-foreground">{n.text}</p>
            ))}
          </div>
        </div>
      )}

      <Dialog open={winOpen} onOpenChange={setWinOpen}>
        <DialogContent className="sm:max-w-md" data-testid="mark-won-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Client won 🎉</DialogTitle>
            <DialogDescription>
              Log the revenue — your Money Health Score, XP, level, streak and analytics all update automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Revenue amount (£)</Label>
              <Input type="number" min="0" className="mt-1.5" value={winAmount}
                onChange={(e) => setWinAmount(e.target.value)} placeholder="e.g. 400" data-testid="win-revenue-input" />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input className="mt-1.5" value={winNote} onChange={(e) => setWinNote(e.target.value)}
                placeholder="e.g. 2-room repaint booked" data-testid="win-revenue-note-input" />
            </div>
            <Button onClick={markWon} disabled={busy} className="w-full bg-emerald-600 text-white hover:bg-emerald-700" data-testid="win-confirm-btn">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trophy className="mr-2 h-4 w-4" />}
              Log revenue & celebrate
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
